/**
 * パス別の submit 時間と、フレーム全体の GPU 時間。
 * パス単位の TIME_ELAPSED は Metal でフレーム時間に寄るので使わない。
 */

export const GPU_PASS_NAMES = [
  'gbuffer',
  'giReservoir',
  'giShade',
  'diReservoir',
  'diShade',
  'denoiser',
  'sunVis',
  'compose',
  'bloom',
  'overlay',
  'dof',
] as const;

export const CPU_PASS_NAMES = ['sdfRebuild'] as const;

export type GpuPassName = (typeof GPU_PASS_NAMES)[number];
export type CpuPassName = (typeof CPU_PASS_NAMES)[number];
export type TimedPassName = GpuPassName | CpuPassName | 'cpuSubmit';

export interface PassTimer {
  begin(name: GpuPassName): void;
  end(): void;
}

export interface PassStats {
  meanMs: number;
  p95Ms: number;
  samples: number;
}

export interface PassTimingSnapshot {
  gpuSupported: boolean;
  passes: Record<string, PassStats>;
}

export const PASS_SAMPLE_WINDOW = 120;

interface DisjointTimerQueryExtension {
  readonly TIME_ELAPSED_EXT: number;
  readonly GPU_DISJOINT_EXT: number;
}

export function mean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const value of values) sum += value;
  return sum / values.length;
}

/** `sorted` は昇順。空なら 0。 */
export function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  const t = Math.min(1, Math.max(0, p));
  const index = Math.floor((sorted.length - 1) * t);
  return sorted[index];
}

export function statsFromSamples(values: readonly number[]): PassStats {
  if (values.length === 0) return { meanMs: 0, p95Ms: 0, samples: 0 };
  const sorted = [...values].sort((a, b) => a - b);
  return {
    meanMs: mean(values),
    p95Ms: percentile(sorted, 0.95),
    samples: values.length,
  };
}

export function pushSample(values: number[], sample: number, window = PASS_SAMPLE_WINDOW): void {
  values.push(sample);
  if (values.length > window) values.shift();
}

/** TIME_ELAPSED がフレーム時間の数倍なら、その環境では使えない。 */
export function gpuQueryTrusted(gpuMs: number, fps: number): boolean {
  if (!(gpuMs > 0) || !(fps > 0)) return false;
  return gpuMs <= 2000 / fps;
}

export interface SnapshotMeta {
  bufferWidth: number;
  bufferHeight: number;
  quality: string;
  denoiser: string;
  scene: string;
  entities: number;
  camera?: string;
  warmup: number;
  samples: number;
  fps: number;
}

export function formatPassSnapshot(meta: SnapshotMeta, timing: PassTimingSnapshot): string {
  const names = [...CPU_PASS_NAMES, ...GPU_PASS_NAMES, 'cpuSubmit'] as const;
  const rows = names.map((name) => {
    const stats = timing.passes[name] ?? { meanMs: 0, p95Ms: 0, samples: 0 };
    return `${name.padEnd(14)} ${stats.meanMs.toFixed(3).padStart(8)} ${stats.p95Ms.toFixed(3).padStart(8)}`;
  });
  const gpu = resolveGpuTotal(timing, meta.fps);
  const gpuLabel = timing.gpuSupported
    ? gpuQueryTrusted(timing.passes.gpuTotal?.meanMs ?? 0, meta.fps)
      ? 'frame-query'
      : 'unreliable'
    : 'unsupported';
  return [
    `draw-bench  ${meta.bufferWidth}x${meta.bufferHeight}  ${meta.quality}  denoiser=${meta.denoiser}`,
    `scene=${meta.scene}  entities=${meta.entities}${meta.camera ? `  camera=${meta.camera}` : ''}`,
    `warmup=${meta.warmup}  samples=${meta.samples}`,
    `gpu  ${gpuLabel}`,
    'pass  cpu-submit',
    '',
    `${'pass'.padEnd(14)} ${'mean'.padStart(8)} ${'p95'.padStart(8)}`,
    ...rows,
    `${'gpuTotal'.padEnd(14)} ${gpu.meanMs.toFixed(3).padStart(8)} ${gpu.p95Ms.toFixed(3).padStart(8)}`,
    `fps            ${meta.fps.toFixed(1)}`,
  ].join('\n');
}

export function resolveGpuTotal(timing: PassTimingSnapshot, fps = 0): PassStats {
  const recorded = timing.passes.gpuTotal;
  if (recorded && recorded.samples > 0 && gpuQueryTrusted(recorded.meanMs, fps)) {
    return recorded;
  }
  let meanMs = 0;
  let p95Ms = 0;
  for (const name of GPU_PASS_NAMES) {
    const stats = timing.passes[name];
    if (!stats) continue;
    meanMs += stats.meanMs;
    p95Ms += stats.p95Ms;
  }
  return { meanMs, p95Ms, samples: 0 };
}

export function timePass(timer: PassTimer | null | undefined, name: GpuPassName, fn: () => void): void {
  timePassValue(timer, name, fn);
}

export function timePassValue<T>(timer: PassTimer | null | undefined, name: GpuPassName, fn: () => T): T {
  if (!timer) return fn();
  timer.begin(name);
  try {
    return fn();
  } finally {
    timer.end();
  }
}

export class GpuPassTimer implements PassTimer {
  private readonly gl: WebGL2RenderingContext;
  private readonly extension: DisjointTimerQueryExtension | null;
  private readonly gpuSamples = new Map<GpuPassName, number[]>();
  private readonly cpuSamples = new Map<string, number[]>();
  private readonly cpuStarted = new Map<string, number>();
  private readonly frameSamples: number[] = [];
  private readonly pendingFrames: WebGLQuery[] = [];
  private activeName: GpuPassName | null = null;
  private activeStarted = 0;
  private frameQuery: WebGLQuery | null = null;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.extension = gl.getExtension('EXT_disjoint_timer_query_webgl2') as DisjointTimerQueryExtension | null;
    for (const name of GPU_PASS_NAMES) this.gpuSamples.set(name, []);
    for (const name of CPU_PASS_NAMES) this.cpuSamples.set(name, []);
    this.cpuSamples.set('cpuSubmit', []);
  }

  get supported(): boolean {
    return this.extension !== null;
  }

  begin(name: GpuPassName): void {
    if (this.activeName) return;
    this.activeName = name;
    this.activeStarted = performance.now();
  }

  end(): void {
    if (!this.activeName) return;
    const bucket = this.gpuSamples.get(this.activeName);
    if (bucket) pushSample(bucket, performance.now() - this.activeStarted);
    this.activeName = null;
  }

  beginFrame(): void {
    if (!this.extension || this.frameQuery || this.pendingFrames.length >= 8) return;
    const query = this.gl.createQuery();
    if (!query) return;
    this.gl.beginQuery(this.extension.TIME_ELAPSED_EXT, query);
    this.frameQuery = query;
  }

  endFrame(): void {
    if (!this.extension || !this.frameQuery) return;
    this.gl.endQuery(this.extension.TIME_ELAPSED_EXT);
    this.pendingFrames.push(this.frameQuery);
    this.frameQuery = null;
  }

  beginCpu(name: TimedPassName): void {
    this.cpuStarted.set(name, performance.now());
  }

  endCpu(name: TimedPassName): void {
    const started = this.cpuStarted.get(name);
    if (started === undefined) return;
    this.cpuStarted.delete(name);
    const bucket = this.cpuSamples.get(name);
    if (!bucket) return;
    pushSample(bucket, performance.now() - started);
  }

  poll(): void {
    if (!this.extension) return;
    if (this.gl.getParameter(this.extension.GPU_DISJOINT_EXT) as boolean) {
      this.dropPendingFrames();
      return;
    }
    while (this.pendingFrames.length > 0) {
      const query = this.pendingFrames[0];
      if (!(this.gl.getQueryParameter(query, this.gl.QUERY_RESULT_AVAILABLE) as boolean)) break;
      const nanoseconds = this.gl.getQueryParameter(query, this.gl.QUERY_RESULT) as number;
      this.gl.deleteQuery(query);
      this.pendingFrames.shift();
      pushSample(this.frameSamples, nanoseconds / 1_000_000);
    }
  }

  reset(): void {
    this.dropPendingFrames();
    this.activeName = null;
    this.frameSamples.length = 0;
    for (const samples of this.gpuSamples.values()) samples.length = 0;
    for (const samples of this.cpuSamples.values()) samples.length = 0;
    this.cpuStarted.clear();
  }

  snapshot(): PassTimingSnapshot {
    const passes: Record<string, PassStats> = {};
    for (const name of GPU_PASS_NAMES) {
      passes[name] = statsFromSamples(this.gpuSamples.get(name) ?? []);
    }
    for (const [name, samples] of this.cpuSamples) {
      passes[name] = statsFromSamples(samples);
    }
    passes.gpuTotal = statsFromSamples(this.frameSamples);
    return { gpuSupported: this.supported, passes };
  }

  private dropPendingFrames(): void {
    if (this.frameQuery && this.extension) {
      this.gl.endQuery(this.extension.TIME_ELAPSED_EXT);
      this.gl.deleteQuery(this.frameQuery);
      this.frameQuery = null;
    }
    for (const query of this.pendingFrames) this.gl.deleteQuery(query);
    this.pendingFrames.length = 0;
  }
}
